import Heading from "@/components/atoms/Heading";
import { SectionHeadingType } from "@/components/atoms/SectionHeading";
import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import useTimer from "@/hooks/useTimer";
import Amplitude from "@/shared/services/amplitude.service";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { isBrowser } from "@/shared/utils/dom";
import { getFromLocal } from "@/shared/utils/storage.util";
import { Typography } from "@graphy/ui";
import classNames from "classnames";
import type { LottieRefCurrentProps } from "lottie-react";
import Image, { StaticImageData } from "next/image";
import {
  MutableRefObject,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Waypoint } from "react-waypoint";

type infoAccordionDataType = {
  title: string;
  description: string;
  image?: {
    src: StaticImageData;
    alt: string;
  };
  ctas?: CtaType[];
};

export enum InfoAccordionTypeEnum {
  MediaFirst = "media-first",
  TextFirst = "text-first",
}

export enum InfoAccFeatSecForAmpEnum {
  Websitebuilder = "website_builder",
  Mobileapp = "mobile_app",
  Marketingtools = "marketing_tools",
  Chatcommunity = "chat_community",
  Mostsecureplatform = "most_secure_platform",
  Graphyassist = "graphy_assist",
  Integratedpaymentgateways = "integrated_payment_gateways",
  Thirdpartysupport = "3rd_party_support",
  Aibrain = "ai-brain"
}

export enum InfoAccProdSecForAmpEnum {
  Courses = "courses",
  Webinars = "webinars",
  Membership = "membership",
  DigitalProducts = "digip",
  AiAvatars = "AiAvatars",
}

export interface InfoAccordionProps {
  title: SectionHeadingType;
  cta?: CtaType;
  commonImage?: {
    src: StaticImageData;
    alt: string;
  };
  data: infoAccordionDataType[];
  containerClassName?: string;
  type: InfoAccordionTypeEnum;
  totalFrames?: number;
  isScrollBased?: boolean;
  amplitudeEvent?: {
    eventName: string;
    eventProps?: { [key: string]: string };
  };
  section?: InfoAccFeatSecForAmpEnum | InfoAccProdSecForAmpEnum;
}

const InfoAccordion = ({
  title,
  cta,
  data,
  containerClassName,
  type,
  totalFrames,
  isScrollBased,
  amplitudeEvent,
  section,
  commonImage,
}: InfoAccordionProps) => {
  const isScrollActivated = isScrollBased ? true : false;
  const { time, isRunning, pauseTimer, startTimer, resetTimer } = useTimer();
  const [resumeTimer, setResumeTimer] = useState(true);

  const timeOutRef = useRef(0);
  const numOfAccordionFeatures = data.length;
  const infoLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const [lottieMwebRefs, setLottieMwebRefs] = useState<
    MutableRefObject<LottieRefCurrentProps | null>[]
  >([]);
  const [activeId, setActiveId] = useState(0);

  const containsCommonImage = useMemo(
    () => Boolean(commonImage),
    [commonImage]
  );
  // Scroll Related code starts here
  useEffect(() => {
    // add or remove refs
    setLottieMwebRefs((lottieMwebRefs) =>
      Array(numOfAccordionFeatures)
        .fill(0)
        .map((_, i) => lottieMwebRefs[i] || createRef())
    );
  }, [numOfAccordionFeatures]);

  const onScroll = useCallback(() => {
    if (totalFrames) {
      const infoAccordionContainer = document.getElementById(
        "info-accordion-container"
      );
      const totalHeight = infoAccordionContainer?.clientHeight || 0;
      const scrollHeight = infoAccordionContainer?.scrollHeight || 0;
      const scrollFromTop = window.scrollY;
      const scrolled = scrollFromTop - scrollHeight + 200;

      if (scrolled > 0 && scrolled < totalHeight * 1.8) {
        const scrollPercentage = (scrolled * 100) / (totalHeight || 1);
        const scrollPercentRounded = Math.floor(scrollPercentage % 100);
        const toDivideBy = Math.floor(100 / numOfAccordionFeatures);
        let scrollIndex = Math.floor(scrollPercentRounded / toDivideBy);

        if (scrollIndex >= numOfAccordionFeatures) {
          scrollIndex = 0;
        }
        setActiveId(scrollIndex);
        const frameNumber =
          ((scrollPercentRounded * totalFrames) / 100) % totalFrames;
        // Check if the current frame is the last frame. If it's the last frame, do nothing. This prevents showing the empty frame at the end.
        if (frameNumber < totalFrames && infoLottieRef.current) {
          infoLottieRef.current.goToAndStop(frameNumber, true);
        } else {
          return;
        }
      }
    }
  }, [totalFrames]);

  useEffect(() => {
    if (isScrollActivated && lottieMwebRefs[activeId]?.current) {
      lottieMwebRefs[activeId]?.current?.goToAndStop(0, true);
    }
  }, [activeId, isScrollActivated, lottieMwebRefs]);

  // useLayoutEffect(() => {
  //   if (isBrowser && isScrollActivated) {
  //     window?.addEventListener("scroll", onScroll);
  //   }
  //   return () => window?.removeEventListener("scroll", onScroll);
  // }, [activeId, isScrollActivated, onScroll]);

  // Scroll Related code ends here

  // Time Related code starts here

  useEffect(() => {
    if (resumeTimer && !isRunning && !isScrollActivated) {
      resetTimer();
      startTimer();
      setResumeTimer(false);
    }
  }, [resumeTimer, isRunning, startTimer, isScrollActivated]);

  useEffect(() => {
    if (time % 5 === 0) {
      setActiveId((activeId) => (activeId + 1) % numOfAccordionFeatures);
    }
  }, [time, numOfAccordionFeatures]);

  // Time Related code ends here

  const renderBody = useCallback(
    (item: infoAccordionDataType) => {
      return (
        <div className={`${isScrollActivated ? "md:h-[150px]" : ""}`}>
          <p
            className={`mt-1 text-body-12 text-grey-4 md:max-w-[280px] md:text-body-16 ${
              type === InfoAccordionTypeEnum.MediaFirst ? "mb-5 md:mb-0 md:max-w-[350px]" : ""
            }`}
          >
            {item.description}
          </p>
          {item.ctas && (
            <div className="mt-3">
              <CtaButtonGroup
                ctas={item.ctas}
                gap="gap-2"
                fullWidthOnMobile={false}
              />
            </div>
          )}
        </div>
      );
    },
    [isScrollActivated, type]
  );

  const renderProgress = useCallback(() => {
    return data.map((item: infoAccordionDataType, index: number) => {
      const progress = (time % 5) * 25;
      return (
        // <div
        //   key={item.title + "progress"}
        //   className={`${
        //     activeId >= index ? "bg-purple-3" : "bg-white"
        //   }  `}
        // ></div>
        <div
          key={index}
          className={classNames(
            "relative mr-1 inline-flex h-[5px] w-[72px] overflow-hidden rounded-xl ",
            {
              "bg-purple-3": index < activeId,
              "bg-white": index >= activeId,
            }
          )}
        >
          {activeId === index && (
            <div
              className="progress-bar-fill"
              style={{
                transform: isRunning
                  ? `translateX(${progress - 100}%)`
                  : "translateX(0%)",
              }}
            />
          )}
        </div>
      );
    });
  }, [activeId, time, data]);

  return (
    <>
      <div className="relative" id="info-accordion-container">
        <div
          className="sticky top-[25%] bg-purple-0 py-4  sm:bg-inherit sm:py-0"
          id="info-accordion-body"
        >
          <div className="container">
            <div className="flex flex-col gap-6 sm:gap-10">
              <div className="flex justify-between">
                <Heading
                  as="h2"
                  fontStyle="text-title-20 sm:text-title-32"
                  heading={title}
                  key={"info-accordion-heading"}
                />
                {cta && (
                  <div className="hidden sm:block">
                    <CtaButtonGroup ctas={[cta]} />
                  </div>
                )}
              </div>

              {/* WEB: */}

              <div
                className={classNames(
                  "hidden w-full justify-between gap-6 rounded-3xl bg-purple-0 p-12 md:flex",
                  { containerClassName },
                  {
                    "flex-row-reverse p-6":
                      type === InfoAccordionTypeEnum.MediaFirst,
                  }
                )}
              >
                <div className="flex-shrink-0 flex-grow-0 basis-[388px]">
                  {renderProgress()}
                  {data.map((item: infoAccordionDataType, index: number) => {
                    return (
                      <details
                        key={index}
                        className="mt-4 w-full"
                        open={index === activeId ? true : false}
                        onClick={(event) => {
                          event.preventDefault();
                          if (activeId !== index) {
                            if (
                              amplitudeEvent?.eventName ===
                              LANDING_PAGE_EVENTS.PRODUCT_DETAIL_EXPANDED.LABEL
                            ) {
                              amplitudeEvent.eventProps = {
                                product: item.title,
                              };
                            }
                            if (
                              amplitudeEvent?.eventName ===
                              LANDING_PAGE_EVENTS
                                .PRODUCT_FEATURE_DETAIL_EXPANDED.LABEL
                            ) {
                              amplitudeEvent.eventProps = {
                                product: section as string,
                                product_feature_title: item.title,
                              };
                            }
                            if (
                              amplitudeEvent?.eventName ===
                              LANDING_PAGE_EVENTS.SUBFEATURE_DETAIL_EXPANDED
                                .LABEL
                            ) {
                              amplitudeEvent.eventProps = {
                                feature: section as string,
                                subfeature_title: item.title,
                              };
                            }
                            if (amplitudeEvent) {
                              new Amplitude().sendEvent({
                                type: amplitudeEvent?.eventName,
                                properties: {
                                  ...amplitudeEvent.eventProps,
                                  current_url: getCurrentURL(),
                                  utm_params: getFromLocal("utmFields"),
                                  screen_width: getScreenWidth(),
                                  referrer: getReferrer(),
                                } as { [key: string]: string | number },
                              });
                            }
                          }

                          if (isScrollBased && isBrowser) {
                            const infoAccordionContainer =
                              document.getElementById(
                                "info-accordion-container"
                              );

                            const scrollHeight =
                              infoAccordionContainer?.scrollHeight || 0;

                            window.scrollTo({
                              top: scrollHeight + (scrollHeight / 4) * index,
                            });
                          } else {
                            pauseTimer();
                            if (isBrowser) {
                              timeOutRef.current = window.setTimeout(() => {
                                // Todo: check why using startTimer() here is not working
                                setResumeTimer(true);
                              }, 15000);
                            }
                          }
                          setActiveId(index);
                        }}
                      >
                        <summary className=" cursor-pointer">
                          <p className="ml-2 inline text-title-24 text-grey-4">
                            {item.title}{" "}
                          </p>
                        </summary>
                        {renderBody(item)}
                      </details>
                    );
                  })}
                </div>
                <div className="hidden md:block">
                  {  containsCommonImage ?  (
                    <Image
                      src={commonImage?.src!}
                      alt={commonImage?.alt!}
                    ></Image>
                  ) : (
                    activeId >= 0 &&
                    data[activeId]?.image && (
                      <Image
                        src={data[activeId].image?.src!}
                        alt={data[activeId].image?.alt!}
                        width={500}
                        height={500}
                      ></Image>
                    )
                  )}
                </div>
              </div>

              {/* MOBILE: */}
              <div className="md:hidden">
                {data.map((item: infoAccordionDataType, index: number) => {
                  const { title } = item;
                  const itemImage = item.image;

                  return (
                    <div key={item.description + "mweb"}>
                      {" "}
                      <Typography
                        fontSize="text-title6"
                        fontWeight="font-semibold"
                        className="mb-1"
                        colorClass="text-grey-4"
                      >
                        {title}
                      </Typography>{" "}
                      { itemImage && (
                        <Image
                          src={itemImage?.src}
                          alt={itemImage?.alt}
                          width={500}
                          height={500}
                        ></Image>
                      )}
                      {renderBody(item)}
                      {/* todo : use classes instead of style */}
                      {type === InfoAccordionTypeEnum.TextFirst &&
                        index !== data.length - 1 && (
                          <hr style={{ color: "#D7D6ED" }} className="my-6" />
                        )}
                    </div>
                  );
                })}

                { containsCommonImage && (
                  <Image
                    src={commonImage?.src!}
                    alt={commonImage?.alt!}
                  ></Image>
                )}
              </div>

              {cta && (
                <div className="sm:hidden">
                  <CtaButtonGroup ctas={[cta]} />
                </div>
              )}
            </div>
          </div>
        </div>

        {isScrollActivated && (
          <div className="sticky top-0 -z-10 hidden md:block">
            {/* <div
              className=""
              style={{
                // position: "relative",
                width: "100%",
                // height: "100vh",
                scrollBehavior: "smooth",
                overflow: "scroll",
                scrollSnapType: "y mandatory",
                // scrollbarColor: "transparent transparent",
                // -ms-overflow-style: none;
                // scrollbar-width: none;
                // msOverflowStyle: "none",
                // scrollbarWidth: "none",
                // Webkit specific style
                // Note: Since React automatically converts camelCase to kebab-case for inline styles, we use Webkit prefix with camelCase

                // Hide scrollbar for Safari and Chrome
              }}
            > */}
            {data.map((_, index: number) => {
              return (
                <Waypoint
                  key={index + "snap-point"}
                  onEnter={() => {
                    setActiveId(index);
                  }}
                  topOffset={"50%"}
                  bottomOffset={"10%"}
                >
                  <div
                    style={{
                      scrollSnapAlign: "center",
                      height: "100vh",
                      scrollSnapStop: "always",
                    }}
                  ></div>
                </Waypoint>
              );
            })}
            <div />
          </div>
          // </div>
        )}
      </div>
    </>
  );
};

export default InfoAccordion;
